import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Row, Col, Carousel, Image } from 'antd';
import { useNavigate, NavLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import '../css/Home.css';

import HeroSection from '../components/HeroSection';
import BlockText from '../components/BlockText';
import FlexText from '../components/FlexText';
import CertificateCarousel from '../components/CertificateCarousel';
import LiquidIntro from '../components/LiquidIntro';

import productsData from '../data/products.json';

const Home = () => {
  // ---------------初始化導航功能---------------
  const navigate = useNavigate(); 
  const AboutUsBtn = () => {
    navigate(isEnglish ? '/en/about' : '/about');
  };

  const AdvantagesBtn = () => {
    navigate(isEnglish ? '/en/advantages' : '/advantages');
  };

  const siteUrl = 'https://www.tangwell.com.tw';
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '湯武生技',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: '湯武生技提供頂級餐飲食品原料，通過FSSC、HACCP、ISO 22000認證，專注食品安全與業界信賴。',
      sameAs: [
        'https://www.facebook.com/share/1C9wRk7y2C/?mibextid=wwXIfr',
        'https://www.instagram.com/tangwell?igsh=MWFqMHc0djI2NDBxYg==',
        'https://www.youtube.com/@%E6%B9%AF%E6%AD%A6-e8w'
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+886-4-7977288',
          contactType: 'customer service',
          email: 'tangwell@tangwell.com.tw',
          areaServed: 'TW',
          availableLanguage: ['Chinese', 'English']
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: siteUrl,
      name: '湯武生技',
      description: '湯武生技提供頂級餐飲食品原料，通過FSSC、HACCP、ISO 22000認證，專注食品安全與業界信賴。',
      publisher: {
        '@type': 'Organization',
        name: '湯武生技',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`
        }
      }
    }
  ];

  // ---------------產品 section---------------
  const [activeKey, setActiveKey] = useState(null);
  // 取得目前選中的產品資料
  const activeProduct = productsData.find(p => p.id === activeKey);
  
  // 沿用與 Header 相同的判斷邏輯 (中英文切換)
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');

  return (
    <main className="main-content">
      <Helmet>
        <title>湯武生技 TANG WU</title>
        <meta name="description" content="湯武生技提供頂級餐飲食品原料，通過FSSC、HACCP、ISO 22000認證，專注食品安全與業界信賴。" />
        <meta name="keywords" content="湯武生技" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <LiquidIntro />
      <HeroSection 
        titleEn="Purpose"
        titleCh="核心目標"
        bgImageName="home-hero.jpg"
        contentCh="以誠信創新研發頂級原料，致力成為全球餐飲方案領導者，為客戶創造價值。"
        contentEn="Driving global catering solutions through integrity, innovation, and premium ingredients to create maximum customer value."
      />

      <FlexText 
        titleEn="Tang Wu"
        titleCh="湯武生技"
        contentCh="湯武生技企業有限公司，主要營業項目為餐飲原料素材之生產，公司業績逐年成長，歷年產品銷往世界各地業績大幅成長，迄今產品也涵蓋歐、亞、美、澳等各國。"
        contentEn="Tang Wu Biotech Enterprise Co., Ltd. specializes in the production of food and beverage ingredients. With consistent annual growth, our global footprint has expanded significantly, with products now distributed across Europe, Asia, the Americas, and Australia."
        onMoreClick={AboutUsBtn}
      />

      {/* video section */}
      <section>
        <div className="container-narrow">
          <div className="video-wrapper">
            {/* 使用 iframe 嵌入 YouTube */}
            <iframe
              className="main-video"
              src="https://www.youtube.com/embed/9EJxcKTC5-Q?autoplay=1&mute=1&loop=1&playlist=9EJxcKTC5-Q"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      <section className="products-section">
        <div className="container-wide">
          <BlockText
            titleEn="Products"
            titleCh="五大產品"
            contentCh="包含果醬調味糖漿系列、冰品果醬系列、椰果蒟蒻系列、布丁豆花系列、三合一調味粉系列、果凍粉系列，經營品牌包括：湯武、活力舒、夏樂多，均為消費這心中的第一選擇。"
            contentEn="Our product range includes fruit syrups, dessert jams, coconut jelly and konjac, pudding and tofu pudding mixes, 3-in-1 flavoring powders, and jelly powders. Our leading brands—Tang Wu, Huo Li Shu, and Xia Le Duo—are the top choices for consumers."
          />

          <Row className="product-flex-container"
            gutter={window.innerWidth <= 768 ? [0, 20] : [0]}
            wrap={window.innerWidth <= 1024} >
            {productsData.map((item) => {
              const isActive = activeKey === item.id;
              return (
                <Col
                  key={item.id}
                  flex={isActive ? "0 0 60%" : "1 0 10%"}
                  className={`product-col ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveKey(isActive ? null : item.id)}
                >
                  <div className="flex-wrapper-row">
                    {/* 左側：產品標題與背景圖（固定寬度防止縮小） */}
                    <div className="product-card">
                      <img
                        src={require(`../images/${item.imgName}`)}
                        alt={item.title}
                        className="card-img"
                        loading="lazy"
                      />

                      <div className={`title-overlay ${isActive ? 'active-layout' : 'center-layout'}`}>
                        <div className="title-ch vertical-text">{item.title}</div>
                        {!isActive && <div className="title-en collapsed-title">{item.en}</div>}
                        {isActive && <div className="title-en horizontal-en">{item.en}</div>}
                      </div>
                    </div>

                    {/* 右側：詳細內容區塊 */}
                    {isActive && (
                      <div className="detail-content-area">
                        <div className="detail-bg-layer">
                          <img
                            src={require(`../images/${item.imgName}`)}
                            alt="背景圖片"
                            className="blur-img"
                            loading="lazy"
                          />
                          <div className="dark-overlay"></div>
                        </div>

                        <div className="detail-text-layer">
                          <div className="info-block">
                            {item.details.categories.map((cat, idx) => (
                              <div key={idx} className="category-group">
                                <ul className="item-list">
                                  {/* 僅擷取前 10 個項目 */}
                                  {cat.items.slice(0, 5).map((li, i) => (
                                    <li key={i} className="product-item-row">
                                      <span className="item-ch">{li.ch}</span>
                                      <span className="item-en">{li.en}</span>
                                    </li>
                                  ))}
                                </ul>                                
                              </div>
                            ))}
                            <div className="more-button-container">
                              <NavLink to="/product" className="more-btn">More</NavLink>
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </section>
      
      {/* Certificates section: text & image parts */}
      <section>
        <FlexText 
          titleEn="Quality"
          titleCh="生產品質"
          contentCh="湯武生技至2009年起，持續朝向目標食品安全健康，符合 歐盟FSSC、HACCP、ISO 22000等證書，且產品亦獲得清真認證，以此管理專案或產品的變更持續將好的食品進行推廣。"
          contentEn="Since 2009, Tang Wu Biotech has remained dedicated to food safety and health. We are certified under FSSC 22000, HACCP, and ISO 22000, and our products are Halal certified. Through these management standards and rigorous change controls, we continuously promote high-quality food products to the global market."
          onMoreClick={AdvantagesBtn}
        />
        <div className="container-wide mb-80">
          <CertificateCarousel filterType="system" slidesToShow={4} variant="home" /> 
        </div>
      </section>
    </main>
  );
};

export default Home;